import type { ComponentPublicInstance, ComputedRef, Ref } from "vue";
import type { DSTabsItem } from "../types/props";

export interface DSTabPublicInstance {
  focus: () => void;
  matchesTarget: (target: EventTarget | null) => boolean;
}

interface UseTabsKeyboardNavigationOptions {
  enabledItems: ComputedRef<DSTabsItem[]>;
  isSelected: (item: DSTabsItem) => boolean;
  items: Readonly<Ref<DSTabsItem[]>>;
  selectItem: (item: DSTabsItem) => void;
}

interface UseTabsKeyboardNavigationResult {
  handleListKeydown: (event: KeyboardEvent) => void;
  setTabRef: (itemValue: string, element: Element | ComponentPublicInstance | null) => void;
}

export const useTabsKeyboardNavigation = ({
  enabledItems,
  isSelected,
  items,
  selectItem,
}: UseTabsKeyboardNavigationOptions): UseTabsKeyboardNavigationResult => {
  const tabRefs = new Map<string, DSTabPublicInstance>();

  const isDSTabPublicInstance = (
    element: Element | ComponentPublicInstance | null
  ): element is ComponentPublicInstance & DSTabPublicInstance => {
    return Boolean(
      element &&
      "focus" in element &&
      typeof element.focus === "function" &&
      "matchesTarget" in element &&
      typeof element.matchesTarget === "function"
    );
  };

  const setTabRef = (itemValue: string, element: Element | ComponentPublicInstance | null) => {
    if (isDSTabPublicInstance(element)) {
      tabRefs.set(itemValue, element);
      return;
    }

    tabRefs.delete(itemValue);
  };

  const findItemFromEvent = (event: KeyboardEvent): DSTabsItem | undefined => {
    return items.value.find(item => tabRefs.get(item.value)?.matchesTarget(event.target));
  };

  const getCurrentItem = (event: KeyboardEvent): DSTabsItem | undefined => {
    return findItemFromEvent(event) ?? items.value.find(isSelected) ?? enabledItems.value[0];
  };

  const getRelativeEnabledItem = (currentItem: DSTabsItem | undefined, offset: number): DSTabsItem | undefined => {
    const currentEnabledItems = enabledItems.value;
    if (!currentEnabledItems.length) return undefined;

    const currentIndex = Math.max(
      currentEnabledItems.findIndex(item => item.value === currentItem?.value),
      0
    );
    const nextIndex = (currentIndex + offset + currentEnabledItems.length) % currentEnabledItems.length;

    return currentEnabledItems[nextIndex];
  };

  const focusItem = (item: DSTabsItem | undefined) => {
    if (!item) return;

    tabRefs.get(item.value)?.focus();
  };

  const handleListKeydown = (event: KeyboardEvent) => {
    const currentItem = getCurrentItem(event);

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        focusItem(getRelativeEnabledItem(currentItem, 1));
        break;
      case "ArrowLeft":
        event.preventDefault();
        focusItem(getRelativeEnabledItem(currentItem, -1));
        break;
      case "Home":
        event.preventDefault();
        focusItem(enabledItems.value[0]);
        break;
      case "End":
        event.preventDefault();
        focusItem(enabledItems.value[enabledItems.value.length - 1]);
        break;
      case " ":
      case "Enter":
        event.preventDefault();
        if (currentItem) selectItem(currentItem);
        break;
    }
  };

  return {
    handleListKeydown,
    setTabRef,
  };
};
